import React, { useEffect, useState, useRef } from 'react';
import useIsMobile from '../../hooks/useIsMobile';
import { Card, Tag } from 'antd';

// Функция для интерполяции между двумя цветами
const interpolateColor = (incorrectCount) => {
    // Определяем крайние цвета:
    // Gray: #b0b0b0 -> rgb(176, 176, 176)
    // Red:   #d70505 -> rgb(215, 5, 5)
    const gray = { r: 176, g: 176, b: 176 };
    const red = { r: 215, g: 5, b: 5 };

    // Ограничиваем incorrectCount максимум до 5
    const count = Math.min(incorrectCount, 5);
    const factor = count / 5;



    // Интерполируем каждую компоненту цвета
    const r = Math.round(gray.r + factor * (red.r - gray.r));
    const g = Math.round(gray.g + factor * (red.g - gray.g));
    const b = Math.round(gray.b + factor * (red.b - gray.b));

    // Преобразуем в шестнадцатеричный формат с ведущими нулями
    const toHex = (num) => num.toString(16).padStart(2, '0');
    return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
};

const IncorrectFrequencyNCorrectCard = ({
                                            items,                // затронутые элементы из /practice/{id}/stats
                                            practiceId,
                                            practiceType,
                                            practiceFormat,
                                            requiredCorrectAnswers,
                                            total,
                                            step = 30,         // горизонтальный промежуток между прямоугольниками
                                            rectWidth = 26,    // ширина прямоугольника
                                            rectHeight = 15,   // высота прямоугольника
                                            verticalGap = 4,   // вертикальный промежуток между строками
                                            rx = 3             // радиус скругления углов
                                        }) => {
    const containerRef = useRef(null);
    const [columns, setColumns] = useState(1);

    const isMobile = useIsMobile();

    // Приходят только затронутые элементы: у нетронутых нет документа прогресса, и
    // их нули достраивать здесь незачем — этот график и так про тех, кто ошибался.
    const processedData = (items || [])
        .map(item => ({
            uuid: item.itemId,
            correctAnswerCount: item.correctAnswerCount || 0,
            incorrectAnswerCount: item.incorrectAnswerCount || 0
        }))
        .sort((a, b) => a.uuid.localeCompare(b.uuid));

    // Обновление числа колонок в зависимости от ширины контейнера
    useEffect(() => {
        const updateColumns = () => {
            if (containerRef.current) {
                const containerWidth = containerRef.current.offsetWidth;
                const newColumns = Math.floor(containerWidth / step);
                setColumns(newColumns > 0 ? newColumns : 1);
            }
        };

        updateColumns();
        window.addEventListener("resize", updateColumns);
        return () => window.removeEventListener("resize", updateColumns);
    }, [step]);


    // Фильтруем только те элементы, у которых correctAnswerCount >= 1 (т.е. имеются попытки)
    const attemptedItems = processedData.filter(item => item.correctAnswerCount >= 1);
    // Считаем, сколько из попыток имеют неправильные ответы (incorrectAnswerCount > 0)
    const errorCount = attemptedItems.filter(item => item.incorrectAnswerCount > 0).length;
    // Вычисляем Error Rate в процентах
    const errorPercentage = attemptedItems.length
        ? Math.round((errorCount / attemptedItems.length) * 100)
        : 0;

    // Расчёт количества строк (минимум одна)
    const rows = Math.max(Math.ceil(processedData.length / columns), 1);
    // Ширина SVG основана на числе колонок и шаге
    const svgWidth = columns * step;
    // Высота SVG рассчитывается на основе высоты прямоугольников и вертикальных промежутков
    const svgHeight = rows * rectHeight + (rows - 1) * verticalGap;

    // Стили для заголовка карточки
    const headerStyle = {
        fontSize: '18px',
        color: '#02796b',
        textAlign: 'center',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis'
    };



    return (
        <Card
            type="inner"
            styles={{
                header: { backgroundColor: '#e5f9f6' },
                body: {
                    padding: '16px',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                },
            }}
            title={<div style={headerStyle}>Incorrect Frequency</div>}
            bordered={false}
        >
            {/* Равномерное распределение тегов с вертикальными разделителями */}
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: '16px' }}>


                <div style={{ flex: 1, textAlign: 'center' }}>
                    <Tag
                        bordered={false}
                        color={'rgba(238,96,2,0.1)'}
                        style={{
                            color: '#ee6002',
                            height: '22px',
                            lineHeight: '22px',
                            marginLeft: '8px'

                        }}
                    >
                        {practiceType}
                    </Tag>
                </div>

                <div style={{ width: '1px', backgroundColor: '#000000', height: '16px' }} />
                <div style={{ flex: 1, textAlign: 'center' }}>
                    <Tag
                        bordered={false}
                        color={'rgba(139,0,0,0.1)'}
                        style={{
                            color: '#8B0000',
                            height: '22px',
                            lineHeight: '22px',
                            marginLeft: '8px'
                        }}
                    >
                        {isMobile ? `Level: 5` : `Incorrect level: 5`}

                    </Tag>
                </div>
                <div style={{ width: '1px', backgroundColor: '#000000', height: '16px' }} />
                <div style={{ flex: 1, textAlign: 'center' }}>
                    <Tag bordered={false} color="#f0f0f0"
                         style={{
                             color: '#808080',
                             height: '22px',
                             lineHeight: '22px',
                             marginLeft: '8px'
                         }}

                    >
                        {isMobile ? `Err: ${errorPercentage}%` : `Error Rate: ${errorPercentage}%`}

                    </Tag>
                </div>
            </div>
            <div ref={containerRef}>
                <svg width="100%" height={svgHeight} viewBox={`0 0 ${svgWidth} ${svgHeight}`}>
                    {processedData.map((item, index) => {
                        const { uuid, incorrectAnswerCount } = item;
                        const col = index % columns;
                        const row = Math.floor(index / columns);
                        // Расчёт позиции прямоугольника по фиксированному горизонтальному шагу
                        const x = col * step;
                        const y = row * (rectHeight + verticalGap);
                        const fillColor = interpolateColor(incorrectAnswerCount);

                        return (
                            <g key={uuid}>
                                <rect
                                    x={x}
                                    y={y}
                                    width={rectWidth}
                                    height={rectHeight}
                                    rx={rx}
                                    style={{ fill: fillColor }}
                                    className="transition"
                                />
                                <text
                                    x={x + rectWidth / 2}
                                    y={y + rectHeight / 2}
                                    fill="white"
                                    fontSize="8"
                                    fontWeight="bold"
                                    textAnchor="middle"
                                    dominantBaseline="middle"
                                >
                                    {index + 1}
                                </text>
                            </g>
                        );
                    })}
                </svg>
            </div>
        </Card>
    );
};

export default IncorrectFrequencyNCorrectCard;
